import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FloatLabelType } from '@angular/material/form-field';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { SelectOutlineOption, SelectOutlineSearchType, SelectOutlineWithPanel } from './select-outline.types';

@Component({
  selector: 'select-outline',
  templateUrl: './select-outline.component.html',
  styleUrls: ['./select-outline.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SelectOutlineComponent implements OnInit, OnChanges {
  @ViewChild('inputRef') inputRef!: ElementRef<HTMLInputElement>;

  control = new FormControl('');

  @Input() options: any[] = [];
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() floatLabelType: FloatLabelType = 'never';
  @Input() disabled: boolean;
  @Input() typeSearch: SelectOutlineSearchType;
  @Input() displayFn?: (option: any) => any;
  @Input() valueFn?: (option: any) => any;
  @Input() selectOutlineWithPanel?: SelectOutlineWithPanel = SelectOutlineWithPanel.default;
  @Input() valueDefault?: string;
  @Input() panelWidth: string = 'auto';

  @Output() outputSelect: EventEmitter<string> = new EventEmitter();
  @Output() outputSearch: EventEmitter<string> = new EventEmitter();
  @Output() outputClear: EventEmitter<boolean> = new EventEmitter();

  isOpenPanel: boolean = true;
  filteredOptions: Observable<any[]>;

  constructor() { }


  ngOnChanges(changes: SimpleChanges): void {
    if (this.options.length > 0) this.observerChangeSearchForm();

    if (this.valueDefault) {
      const exist = this.options.find(option => this.valueFn(option) === this.valueDefault);
      this.control.setValue(exist ?? '');
    } else {
      this.control.setValue('');
    }
  }

  ngOnInit(): void {
    this.displayFn = this.displayFn || ((option: SelectOutlineOption) => option.label);
    this.valueFn = this.valueFn || ((option: SelectOutlineOption) => option.value);

    if (this.disabled) {
      this.control.disable()
      return;
    }

    if (this.valueDefault) {
      const exist = this.options.find(option => this.valueFn(option) === this.valueDefault);
      if (exist) {
        this.control.setValue(exist);
      }
    }
  }

  onSelect(value: string) {
    this.outputSelect.emit(this.valueFn(value));
    this.blurInput();
  }

  onClear(): void {
    this.control.reset('', { emitEvent: false });
    this.outputClear.emit(true);
    this.observerChangeSearchForm();
    this.blurInput();
  }

  openOrClosePanel(event: Event, triggerC: MatAutocompleteTrigger): void {
    event.stopPropagation();

    this.isOpenPanel ? triggerC.openPanel() : triggerC.closePanel();
    this.isOpenPanel = !this.isOpenPanel;
  }

  blurInput(): void {
    this.inputRef.nativeElement.blur();
  }

  observerChangeSearchForm() {
    this.filteredOptions = this.control.valueChanges
      .pipe(
        startWith(''),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(value => of(this._filter(value)))
      );
  }

  private _filter(value?: string): string[] {
    if (!value) return this.options;

    const filterValue = typeof value === 'string' ? value.toLowerCase() : this.valueFn(value)?.toLowerCase() ?? '';
    const exist = this.options.filter(option =>
      this.displayFn(option).toLowerCase().includes(filterValue)
    );

    if (this.typeSearch === 'service' && filterValue && exist.length === 0) {
      this.outputSearch.emit(filterValue);
      return [];
    }

    return exist;
  }
}
